import React, { useState, useCallback } from 'react';
import { 
  CloudArrowUpIcon, 
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { useDuplicateFileCheck, DuplicateFileResult } from '../services/duplicateFileService';

interface FileUploadAreaProps {
  onFilesUploaded: (files: File[]) => void;
  uploadedFiles: File[];
  maxFileSize?: number; // in bytes
  allowedTypes?: string[];
  realmId: string;
}

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const ALLOWED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/rtf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/bmp',
  'image/tiff'
];

export const FileUploadArea: React.FC<FileUploadAreaProps> = ({
  onFilesUploaded,
  uploadedFiles,
  maxFileSize = MAX_FILE_SIZE,
  allowedTypes = ALLOWED_TYPES,
  realmId,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [duplicateWarnings, setDuplicateWarnings] = useState<Map<string, DuplicateFileResult>>(new Map());
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);
  
  const { checkMultipleFiles } = useDuplicateFileCheck();

  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > maxFileSize) {
      return `File "${file.name}" is too large. Maximum size is ${formatFileSize(maxFileSize)}.`;
    }

    // Check file type
    if (!allowedTypes.includes(file.type)) {
      return `File "${file.name}" has an unsupported type. Allowed types: ${allowedTypes.join(', ')}.`;
    }

    return null;
  };

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const newErrors: string[] = [];
    const validFiles: File[] = [];

    fileArray.forEach(file => {
      const error = validateFile(file);
      if (error) {
        newErrors.push(error);
      } else {
        validFiles.push(file);
      }
    });

    setErrors(newErrors);

    if (validFiles.length > 0) {
      // Check for duplicates before adding files
      setIsCheckingDuplicates(true);
      try {
        const duplicateResults = await checkMultipleFiles(validFiles, realmId);
        setDuplicateWarnings(duplicateResults);
      } catch (error) {
        console.error('Error checking for duplicates:', error);
      } finally {
        setIsCheckingDuplicates(false);
      }

      const allFiles = [...uploadedFiles, ...validFiles];
      onFilesUploaded(allFiles);
    }
  }, [uploadedFiles, onFilesUploaded, maxFileSize, allowedTypes, checkMultipleFiles, realmId]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
  }, [handleFiles]);

  const removeFile = useCallback((index: number) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    onFilesUploaded(newFiles);
  }, [uploadedFiles, onFilesUploaded]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (file: File) => {
    if (file.type.includes('pdf')) {
      return '📄';
    } else if (file.type.includes('word') || file.type.includes('document')) {
      return '📝';
    } else if (file.type.includes('excel') || file.type.includes('spreadsheet')) {
      return '📊';
    } else if (file.type.includes('powerpoint') || file.type.includes('presentation')) {
      return '📈';
    } else if (file.type.includes('image')) {
      return '🖼️';
    } else {
      return '📎';
    }
  };

  return (
    <div className="space-y-4">
      {/* Drag and Drop Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
          isDragOver
            ? 'border-green-400 bg-green-50 dark:bg-green-900/20'
            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <CloudArrowUpIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" />
        <div className="space-y-1">
          <p className="text-base font-medium text-gray-900 dark:text-white">
            Drop files here or click to browse
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Supports PDF, Word, Excel, PowerPoint, images, and text files
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Maximum file size: {formatFileSize(maxFileSize)}
          </p>
        </div>
        
        <input
          type="file"
          multiple
          accept={allowedTypes.join(',')}
          onChange={handleFileInput}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
      </div>

      {/* Error Messages */}
      {errors.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
            <h4 className="font-medium text-red-800 dark:text-red-200">
              Upload Errors
            </h4>
          </div>
          <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
            {errors.map((error, index) => (
              <li key={index}>• {error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Duplicate Warnings */}
      {duplicateWarnings.size > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <ExclamationCircleIcon className="h-5 w-5 text-yellow-600" />
            <h4 className="font-medium text-yellow-800 dark:text-yellow-200">
              Duplicate File Warnings
            </h4>
          </div>
                              <div className="text-sm text-yellow-700 dark:text-yellow-300 space-y-2">
                      {Array.from(duplicateWarnings.entries()).map(([fileName, result]) => (
                        <div key={fileName} className="flex items-start gap-2">
                          <span className="text-yellow-600">•</span>
                          <div className="flex-1">
                            <p className="font-medium">{fileName}</p>
                            {result.existingFileName && (
                              <p className="text-xs">
                                This file appears to be identical to: <span className="font-medium">{result.existingFileName}</span>
                              </p>
                            )}
                            {/* Enhanced Client and Matter Information */}
                            {(result.existingClientName || result.existingMatterNumber) && (
                              <div className="mt-1 space-y-1">
                                {result.existingClientName && (
                                  <p className="text-xs">
                                    <span className="font-medium">Client:</span> {result.existingClientName}
                                    {result.existingClientId && (
                                      <span className="text-yellow-500 ml-1">(ID: {result.existingClientId.substring(0, 8)}...)</span>
                                    )}
                                  </p>
                                )}
                                {result.existingMatterNumber && (
                                  <p className="text-xs">
                                    <span className="font-medium">Matter:</span> {result.existingMatterNumber}
                                    {result.existingMatterId && (
                                      <span className="text-yellow-500 ml-1">(ID: {result.existingMatterId.substring(0, 8)}...)</span>
                                    )}
                                  </p>
                                )}
                              </div>
                            )}
                            {result.message && (
                              <p className="text-xs italic mt-1">{result.message}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
        </div>
      )}

      {/* Checking Duplicates Indicator */}
      {isCheckingDuplicates && (
        <div className="bg-lex-accent-soft border border-lex-accent rounded-lg p-4">
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-lex-accent"></div>
            <span className="text-sm text-lex-accent">
              Checking for duplicate files...
            </span>
          </div>
        </div>
      )}

      {/* Uploaded Files List */}
      {uploadedFiles.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
            <CheckCircleIcon className="h-5 w-5 text-green-600" />
            Uploaded Files ({uploadedFiles.length})
          </h4>
          
          <div className="space-y-2">
            {uploadedFiles.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{getFileIcon(file)}</span>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white text-sm">
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatFileSize(file.size)} • {file.type}
                    </p>
                  </div>
                </div>
                
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                  title="Remove file"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Total Size */}
          <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
            Total size: {formatFileSize(uploadedFiles.reduce((sum, file) => sum + file.size, 0))}
          </div>
        </div>
      )}


    </div>
  );
}; 